<script lang="ts" setup>
  import { TaskTypeTransformMap } from '@api/models/task';
  import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/vue-query';
  import type { Cell } from '@tanstack/vue-table';
  import { storeToRefs } from 'pinia';
  import { computed, onBeforeUnmount, ref, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';

  import type { Task } from '@stuut/api/models';
  import type { TaskQueryParams } from '@stuut/api/query';
  import { humanize } from '@stuut/common/src/utils';
  import { ScrollArea } from '@ui/components/scroll-area';
  import { ModelTable } from '@ui/components/table';

  import { CustomerFilter } from '@/components/filters';
  import { TaskTableDrawerColumns } from '@/components/tasks/table/columns';
  // import StuutLoading from '@/components/common/StuutLoading.vue';
  import { usePaginatedTable, usePersistentSelection } from '@/composables';
  import { useProfile, useTaskCollectionStore } from '@/store';

  // interface Props {
  //   partner?: Partner;
  //   classifications?: string[];
  // }

  // const props = defineProps<Props>();
  const route = useRoute();
  const router = useRouter();
  const profileStore = useProfile();
  const taskCollectionStore = useTaskCollectionStore();
  // const isAdmin = computed<boolean>(() => profileStore.profile.is_org_admin);
  const totalRecords = ref<number>();
  const { isTaskTypeGroupView } = storeToRefs(taskCollectionStore);

  // Use the shared query params from the store
  const queryParams = ref<TaskQueryParams>(taskCollectionStore?.initializeSharedQueryParams());

  const queryClient = useQueryClient();

  onBeforeUnmount(() => {
    queryClient.removeQueries({ exact: false, queryKey: ['tasksDrawerTable'] });
  });

  const query = useQuery<Task[], any>({
    enabled: computed(() => Boolean(profileStore.profile?.id)),

    initialData: [], // fallback / default value
    placeholderData: keepPreviousData,
    queryFn: async () => {
      try {
        totalRecords.value = taskCollectionStore.currentCollectionTasksTotal;
        return taskCollectionStore.currentCollectionTasks;
      } catch (e) {
        console.error(e);
        return [];
      }
    },
    queryKey: ['tasksDrawerTable', queryParams.value],
    refetchOnWindowFocus: false,
  });

  const isPageLoading = computed(
    () => query.isLoading.value || query.isFetching.value || !query.isFetchedAfterMount.value,
  );

  const { clearSelection, selectedItems, updateSelection } = usePersistentSelection<Task>(
    (id: string) => taskCollectionStore.getById(id),
  );

  const onCellClicked = async (cell: Cell<Task, unknown>, event: MouseEvent) => {
    if (event.metaKey || event.ctrlKey) {
      const taskRoute = router.resolve({
        query: {
          ...(route.query ?? {}),
          task_drawer_id: cell.row.original.id,
        },
      });
      window.open(taskRoute.href, '_blank', 'noopener,noreferrer');
    } else {
      if (['task_assignee'].includes(cell.column.id)) {
        return event.preventDefault();
      }

      taskCollectionStore.openTaskDrawer({
        taskId: cell.row.original.id,
        taskView: 'stack',
      });
    }
  };

  const { table } = usePaginatedTable<Task, TaskQueryParams>({
    params: queryParams as any,
    query,
    selectItemsWithIds: updateSelection,
    tableOptions: {
      get columns() {
        return TaskTableDrawerColumns;
      },
      enableColumnResizing: false,
      getRowId: (row) => row.id,
    },
    useRouterQuery: true,
  });

  const clearSelectedItems = () => {
    clearSelection?.();
    table?.setRowSelection?.({});
  };

  const updatePartnerFilter = async (id?: string) => {
    taskCollectionStore.setListPartnerFilter(id);
    taskCollectionStore.updateSharedQueryParams({ page: 0 });

    await taskCollectionStore.updateFromQps(route);
    taskCollectionStore.refreshTaskDrawerTable();
  };

  watch(
    () => route.query,
    async (currQPs, oldQPs) => {
      if (
        taskCollectionStore.isLoadingTask ||
        taskCollectionStore.hasValidTaskDrawerChanges(currQPs, oldQPs)
      ) {
        return;
      }

      const { values } = taskCollectionStore.getUpdatedQPs(currQPs, oldQPs);

      if (values?.page) {
        await taskCollectionStore.setCollectionTasks(route.query);
        taskCollectionStore.refreshTaskDrawerTable();
      }
    },
  );

  defineExpose({
    clearSelectedItems,
    selectedItems,
    setRowSelection: table?.setRowSelection,
    table,
    totalRecords,
  });
</script>

<template>
  <ScrollArea class="flex-1 h-full [&>div]:outline-transparent @container">
    <CustomerFilter
      v-if="isTaskTypeGroupView"
      class="w-fit mb-2.5"
      query-name="task_list_partner"
      btn-size="sm"
      @update="(partner) => updatePartnerFilter(partner?.id)"
    />

    <div class="h-full p-5 space-y-4 rounded border-2 overflow-y-auto">
      <div v-if="isTaskTypeGroupView" class="space-y-4">
        <div class="font-semibold">
          {{
            TaskTypeTransformMap[route.query.group_view_id as string] ||
            humanize(route.query.group_view_id as string)
          }}
        </div>
      </div>

      <div class="grid grid-rows-[1fr_min-content] overflow-hidden">
        <div
          v-if="taskCollectionStore?.isLoadingTask || taskCollectionStore?.isLoadingCollectionTasks"
          class="flex flex-col gap-4 flex-1 py-12 pl-5 w-full bg-secondary"
        >
          <div class="flex items-center relative">
            <div class="absolute w-full">
              <div
                class="text-xs flex flex-nowrap items-center w-full place-content-center space-x-4"
              >
                <!-- <StuutLoading /> -->

                <div class="space-y-1">
                  <strong>Hang tight!</strong>
                  <div class="text-muted-foreground">
                    The AI agent is gathering some tasks that need your expert touch...
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <ModelTable
          v-else
          class="task-drawer-table"
          :columns="TaskTableDrawerColumns"
          :loading="isPageLoading"
          :no-search-result="!table.getRowModel().rows?.length"
          :table="table"
          :edge="false"
          row-hover-class="hover:bg-accent"
          keep-selection
          @cell-click="onCellClicked"
        />
      </div>
    </div>
  </ScrollArea>
</template>

<style lang="postcss">
  @reference "../../../globals.css";

  .task-drawer-table tr {
    td:first-child,
    th:first-child {
      @apply pl-0;
    }
  }

  .task-drawer-table {
    tr {
      @apply cursor-pointer;
    }
  }
</style>
